import React, { useEffect, useState } from "react";
import { History, IndianRupee, Footprints, ClipboardList, ClipboardCheck, XCircle, FileText, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { StaffAvatar, EmpCode } from "./StaffAvatar";
import { AuditTimeline } from "./AuditTimeline";
import { api, errMsg, fmtDateTime, fmtINR } from "../lib/api";

/**
 * Phase 90 — everything attributed to one staff member (collections, visits, assessments,
 * approvals given, configuration changes). Works for deactivated users and for users whose
 * record was removed — the identity is rebuilt from the permanent snapshots.
 */
const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="rounded-lg border bg-card p-3" data-testid={testId}>
    <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
    <p className="text-lg font-semibold tabular-nums leading-tight mt-0.5">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

export function StaffActivitySheet({ code, user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    setData(null); setError(null); setLoading(true);
    api.get(`/t/${code}/users/${userId}/activity`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, [code, userId]);

  const profile = data?.user || user;
  const counts = data?.counts;

  return (
    <Sheet open={!!user} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="staff-activity-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-3">
            <StaffAvatar code={code} userId={profile?.id} name={profile?.name} photoUrl={profile?.photo_url} size="lg" />
            <span className="min-w-0">
              <span className="block truncate" data-testid="staff-activity-name">{profile?.name || "Former staff member"}{profile?.name ? <EmpCode code={profile?.employee_id} className="text-xs" /> : null}</span>
              <span className="block text-xs font-normal text-muted-foreground">
                {[profile?.role_label || profile?.role,
                  profile?.designation && profile.designation.trim().toLowerCase() !== (profile?.role_label || profile?.role || "").trim().toLowerCase() ? profile.designation : null,
                ].filter(Boolean).join(" • ") || "—"}
              </span>
            </span>
            {profile?.deleted ? (
              <Badge variant="outline" className="ml-auto border-slate-300 text-slate-600" data-testid="staff-activity-status">Record removed</Badge>
            ) : profile?.is_active === false ? (
              <Badge variant="outline" className="ml-auto border-amber-300 bg-amber-50 text-amber-800" data-testid="staff-activity-status">Deactivated</Badge>
            ) : (
              <Badge variant="outline" className="ml-auto border-emerald-300 bg-emerald-50 text-emerald-800" data-testid="staff-activity-status">Active</Badge>
            )}
          </SheetTitle>
          <SheetDescription className="flex items-center gap-1.5 text-xs">
            <ShieldCheck className="h-3.5 w-3.5" /> Activity history is retained permanently — it stays attributed to this person even after deactivation, transfer or resignation.
          </SheetDescription>
        </SheetHeader>

        {error ? (
          <div className="mt-6 text-sm text-destructive flex items-center justify-between gap-2" data-testid="staff-activity-error">
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={() => { setError(null); setLoading(true); api.get(`/t/${code}/users/${userId}/activity`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))).finally(() => setLoading(false)); }}>Retry</Button>
          </div>
        ) : (
          <div className="mt-5 space-y-5">
            {loading || !counts ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="staff-activity-loading">
                {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="staff-activity-counts">
                <Stat icon={IndianRupee} label="Collections" value={counts.payments} sub={fmtINR(counts.payments_amount)} testId="staff-activity-payments" />
                <Stat icon={Footprints} label="Field visits" value={counts.visits} testId="staff-activity-visits" />
                <Stat icon={ClipboardList} label="Re-assessments" value={counts.reassessments_submitted} sub="submitted" testId="staff-activity-reassessments" />
                <Stat icon={FileText} label="New surveys" value={counts.surveys_submitted} sub="submitted" testId="staff-activity-surveys" />
                <Stat icon={ClipboardCheck} label="Approvals given" value={counts.approvals} testId="staff-activity-approvals" />
                <Stat icon={XCircle} label="Rejections given" value={counts.rejections} testId="staff-activity-rejections" />
              </div>
            )}
            {data && (
              <p className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3" data-testid="staff-activity-range">
                <span><History className="inline h-3 w-3 mr-1" />First activity: {fmtDateTime(data.first_activity_at)}</span>
                <span>Latest: {fmtDateTime(data.last_activity_at)}</span>
                <span>{counts?.audit_entries ?? 0} audit entries</span>
              </p>
            )}
            <div>
              <p className="text-sm font-medium mb-3">Timeline</p>
              <AuditTimeline code={code} events={data?.events} loading={loading} showPin
                emptyText="No attributed activity yet for this staff member." />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
